/**
 * おすすめ求人（Workgate + Carat 混在）の状態と操作。
 *
 * MyPage / WorkgateDialog の2画面で使うため composable に置く
 * （CLAUDE.md: Composition の置き場 3 — 複数 SFC で再利用）。
 * 通信は services/recommend-jobs-client（JSON）に委ね、ここでは状態と
 * 提供元ごとの分岐だけを扱う。トースト表示は呼び出し側 SFC の責務。
 */
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import {
  applyCaratJob as postCaratApply,
  applyWorkgateJob as postWorkgateApply,
  fetchCaratDetail,
  fetchRecommendJobs,
  fetchWorkgateDetail,
  extractApplyError
} from '@/services/recommend-jobs-client'
import type {
  CaratApplyRequest,
  RecommendJobsRequest,
  WorkgateApplyRequest
} from '@/services/recommend-jobs-client'
import {
  caratDuplicateGroupKey,
  toApplicantOccupation,
  toRecommendDetailShape,
  trimCaratJobName,
  useRecommendJobBlocks
} from '@/composables/useRecommendJobBlocks'
import type {
  PositionedOffer,
  RecommendLayout,
  RecommendProviderGroup
} from '@/composables/useRecommendJobBlocks'

const PROVIDER_CARAT = 'carat'
const PROVIDER_WORKGATE = 'workgate'

/** Carat 応募に必要な、SFC 側から渡す文脈 */
export interface CaratApplyContext {
  uuid: string
  formatId: string
  deviceFlag: string
  appFlag: string
  siteFlag: string
}

/** 応募の結果。SFC 側がこれを見てトーストを出す */
export interface ApplyResult {
  ok: boolean
  /** テスト氏名のため提供元へ送信されなかった（DBにも残らない） */
  skippedAsTest?: boolean
  /** 提供元が応募済みと判定した。カードは応募済みへ落とす */
  alreadyApplied?: boolean
  /** 失敗時に画面へ出す文言。ok=false なら必ず入る */
  message?: string
  /**
   * 画面の通知種別。失敗の重さは呼び出し側ごとに判断させず、ここで確定させる。
   * 送信されていない（テスト氏名）・応募済みは異常ではないので warning。
   */
  messageType?: 'warning' | 'error'
}

/** 失敗結果を1か所で組み立て、文言と通知種別の対応を崩さない */
function applyFailure(
  message: string,
  extra: Partial<ApplyResult> = {}
): ApplyResult {
  const soft = extra.skippedAsTest || extra.alreadyApplied
  return {
    ok: false,
    message,
    messageType: soft ? 'warning' : 'error',
    ...extra
  }
}

/**
 * 取得結果に応募済みの Carat 求人があれば、同じプレフィックスの求人も応募済みに寄せる。
 *
 * サーバー（`_applied_carat_ids`）は job_id 完全一致で is_applied を立てるため、
 * ダイアログを開き直すと同一クライアントの別求人が「応募できる顔」で戻ってくる。
 * 押せば必ず DUPLICATE_APPLICATION になるので、表示前に落としておく。
 * 応募済みの求人が結果に含まれない場合は判定材料が無い（サーバー側の宿題）。
 */
export function propagateCaratAppliedGroups(
  groups: RecommendProviderGroup[] | null | undefined
): RecommendProviderGroup[] {
  const list = groups || []
  for (const group of list) {
    if (!group || group.provider !== PROVIDER_CARAT) continue
    const offers = group.offers || []
    const appliedKeys: string[] = []
    for (const offer of offers) {
      if (!offer || offer.is_applied !== true) continue
      const key = caratDuplicateGroupKey(offer.job_id)
      if (key && appliedKeys.indexOf(key) < 0) appliedKeys.push(key)
    }
    if (appliedKeys.length === 0) continue
    for (const offer of offers) {
      if (!offer || offer.is_applied === true) continue
      const key = caratDuplicateGroupKey(offer.job_id)
      if (key && appliedKeys.indexOf(key) >= 0) offer.is_applied = true
    }
  }
  return list
}

export function useRecommendJobs(layout: RecommendLayout) {
  const providers: Ref<RecommendProviderGroup[]> = ref([])
  const loaded = ref(false)
  const keyword = ref('')
  /** 送信中の求人キー（`provider-job_id`）。同一求人の二重送信を止める */
  const applyingKey = ref('')

  const { blocks, blockSize, totalCount } = useRecommendJobBlocks(
    providers,
    layout
  )
  const hasOffers = computed(() => totalCount.value > 0)

  const offerKey = (offer: { provider?: string; job_id?: string }) =>
    `${offer.provider || ''}-${offer.job_id || ''}`

  const isApplying = (offer: { provider?: string; job_id?: string }) =>
    !!applyingKey.value && applyingKey.value === offerKey(offer)

  /** 取得。失敗しても throw せず空で確定させる（枠が空になるだけ） */
  async function load(params: RecommendJobsRequest): Promise<void> {
    try {
      const data = await fetchRecommendJobs(params)
      // 前回の応募が残っている場合、同グループも応募済みへ寄せてから表示する
      providers.value = propagateCaratAppliedGroups(data.providers)
      keyword.value = data.keyword
    } catch (e) {
      providers.value = []
      keyword.value = ''
    } finally {
      loaded.value = true
    }
  }

  /**
   * 応募成功時に元データ側の is_applied を立てる。
   * ブロックの offer はコピーなので、ここを更新しないと表示が戻る。
   *
   * Carat は同一クライアント（job_id のプレフィックス）の求人をまとめて
   * DUPLICATE_APPLICATION で弾くため、同じグループの求人も応募済みにする。
   * 押せば必ず失敗するボタンを残すより、応募できたはずの求人を1つ閉じる方が軽い。
   * Workgate は 409 を求人単位で返すので完全一致のまま（提供元ごとに分岐する）。
   */
  function markApplied(offer: { provider?: string; job_id?: string }): void {
    const provider = offer.provider || ''
    const jobId = String(offer.job_id == null ? '' : offer.job_id)
    if (!jobId) return
    const groupKey =
      provider === PROVIDER_CARAT ? caratDuplicateGroupKey(jobId) : ''
    for (const group of providers.value) {
      if (!group || group.provider !== provider) continue
      for (const item of group.offers || []) {
        const itemId = String(item.job_id == null ? '' : item.job_id)
        // 完全一致に加え、Carat は同一プレフィックスも応募済みへ落とす
        if (
          itemId === jobId ||
          (!!groupKey && caratDuplicateGroupKey(itemId) === groupKey)
        ) {
          item.is_applied = true
        }
      }
    }
  }

  async function applyCarat(
    offer: PositionedOffer,
    source: 'list' | 'detail',
    ctx: CaratApplyContext
  ): Promise<ApplyResult> {
    applyingKey.value = offerKey(offer)
    const payload: CaratApplyRequest = {
      uuid: ctx.uuid,
      job_id: String(offer.job_id),
      search_id: offer.search_id || '',
      format_id: ctx.formatId,
      // 応募者の区分。求人の雇用形態を送ると応募者情報が誤る
      occupation: toApplicantOccupation(ctx.formatId),
      detail_flag: source === 'detail' ? 'd' : '',
      keyword: keyword.value || '',
      area: offer.pref_name || '',
      display_position: offer.displayPosition || 0,
      block_index: offer.blockIndex || 0,
      device_flg: ctx.deviceFlag,
      app_flag: ctx.appFlag,
      site_flag: ctx.siteFlag
    }
    try {
      const res = await postCaratApply(payload)
      if (res.test === 'ok') {
        // 氏名・カナに「テスト」等が含まれる会員は提供元へ送信されない
        return applyFailure('テストデータのため送信されていません', {
          skippedAsTest: true
        })
      }
      if (res.result !== 'ok') {
        return applyFailure(res.message || '応募できませんでした')
      }
      markApplied(offer)
      return { ok: true }
    } catch (e) {
      // varuna は応募失敗を HTTP 400 で返すため、本文は catch 側で読む
      const err = extractApplyError(e)
      if (err.error_code === 'DUPLICATE_APPLICATION') {
        // 同じ会社に応募済み。再送しても必ず失敗するのでカードを応募済みにする
        markApplied(offer)
        return applyFailure('すでに応募済みの企業です', {
          alreadyApplied: true
        })
      }
      return applyFailure(err.message || '応募失敗しました')
    } finally {
      applyingKey.value = ''
    }
  }

  async function applyWorkgate(
    offer: PositionedOffer,
    payload: WorkgateApplyRequest
  ): Promise<ApplyResult> {
    applyingKey.value = offerKey(offer)
    try {
      await postWorkgateApply(payload)
      markApplied(offer)
      return { ok: true }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        markApplied(offer)
        return applyFailure('すでに応募済みです', { alreadyApplied: true })
      }
      return applyFailure('応募失敗しました')
    } finally {
      applyingKey.value = ''
    }
  }

  /**
   * 詳細データを組む。Workgate は詳細APIを引き、失敗したら一覧データで代替する。
   * Carat は一覧レスポンスに説明文まで含まれるためそれを整形して使う。
   */
  async function buildDetail(
    offer: PositionedOffer,
    ctx?: CaratApplyContext
  ): Promise<Record<string, unknown> | null> {
    if (offer.provider === PROVIDER_CARAT) {
      const base = toRecommendDetailShape(offer)
      // 文脈が無い呼び出し（マイページ等）は一覧データのままにする。
      // 募集職種・本社所在地は '-' 表示になるが、詳細取得より縮退を優先する
      if (!base || !ctx) return base
      let detail: Record<string, unknown> | null = null
      try {
        detail = await fetchCaratDetail({
          uuid: ctx.uuid,
          job_id: String(offer.job_id || ''),
          search_id: String(offer.search_id || ''),
          format_id: ctx.formatId,
          device_flg: ctx.deviceFlag,
          site_flag: ctx.siteFlag,
          app_flag: ctx.appFlag
        })
      } catch (e) {
        detail = null
      }
      if (!detail) return base
      // 一覧が持たない項目だけ上書きする。詳細APIの生形は Step 2 の
      // 整形関数（給与・画像）が期待する形と違うので丸ごと差し替えない
      const workLocation =
        typeof detail.work_location === 'string' ? detail.work_location : ''
      return {
        ...base,
        job_name: trimCaratJobName(detail.job),
        company_address:
          typeof detail.company_address === 'string'
            ? detail.company_address
            : '',
        // 一覧の cities は「東京都(立川市)」までだが、詳細は番地まで持っている。
        // カード側（location_text）は一覧の短い表記のままにして、
        // 詳細画面の勤務地だけ完全な住所へ差し替える
        place: workLocation || base.place
      }
    }
    const detail = await fetchWorkgateDetail(String(offer.job_id))
    if (!detail) return toRecommendDetailShape(offer)
    // 詳細APIの生レスポンスには識別子と計測用の順位が無い。ここで引き継ぐ
    return {
      ...detail,
      provider: offer.provider || PROVIDER_WORKGATE,
      job_id: offer.job_id,
      displayPosition: offer.displayPosition || 0,
      blockIndex: offer.blockIndex || 0,
      apply_enabled: offer.apply_enabled !== false,
      is_applied: offer.is_applied === true
    }
  }

  return {
    providers,
    loaded,
    keyword,
    applyingKey,
    blocks,
    blockSize,
    totalCount,
    hasOffers,
    isApplying,
    load,
    markApplied,
    applyCarat,
    applyWorkgate,
    buildDetail
  }
}
